<svelte:options
  customElement={{
    tag: "oc-promo-card-v1",

    extend: window.__components.extend(),
    props: {
      asButton: { type: "Boolean", attribute: "as-button" },
      backgroundColor: { type: "String", attribute: "background-color" },
      base64Href: { type: "String", attribute: "base64-href" },
      elevationLevel: { type: "String", attribute: "elevation-level" },
      footerHeadline: { type: "String", attribute: "footer-headline" },
      footerHeadlineLevel: { type: "Number", attribute: "footer-headline-level" },
      footerText: { type: "String", attribute: "footer-text" },
      href: { type: "String" },
      inverted: { type: "Boolean" },
      mainHeadline: { type: "String", attribute: "main-headline" },
      headerHeadline: { type: "String", attribute: "header-headline" },
      mainHeadlineLevel: { type: "Number", attribute: "main-headline-level" },
      headerHeadlineLevel: { type: "Number", attribute: "header-headline-level" },
      ocAriaVisibleElements: { type: "Array", attribute: "oc-aria-visible-elements" },
      overline: { type: "String" },
      headerOverline: { type: "String", attribute: "header-overline" },
      rel: { type: "String" },
      target: { type: "String" },
    },
  }}
/>

<script lang="ts">
  import type { OcAriaVisibleElement, Props } from "./PromoCardV1.types.js";
  import { useSlots } from "../../../common/utils/useSlots.svelte";
  import BasicCardV2 from "../../../common/components/Card/BasicCardV2.svelte";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";

  let {
    backgroundColor = undefined,
    mainHeadline = undefined,
    headerHeadline: headerHeadlineProp = undefined,
    mainHeadlineLevel = undefined,
    headerHeadlineLevel: headerHeadlineLevelProp = undefined,
    overline = undefined,
    headerOverline: headerOverlineProp = undefined,
    elevationLevel = "canvas",
    footerHeadline = undefined,
    footerHeadlineLevel = undefined,
    footerText = undefined,
    asButton = false,
    href = undefined,
    inverted = false,
    base64Href = undefined,
    ocAriaVisibleElements = [
      "overline",
      "headerOverline",
      "mainHeadline",
      "headerHeadline",
      "logo",
      "headerLogo",
      "tags",
      "mainContent",
      "footerHeadline",
      "footerText",
    ],
    rel = undefined,
    target = undefined,
  }: Props = $props();

  const Host = $host<HTMLElement>();

  const slots = useSlots(Host);

  let headerHeadline = $derived(headerHeadlineProp ?? mainHeadline);
  let headerHeadlineLevel = $derived(headerHeadlineLevelProp ?? mainHeadlineLevel);
  let headerOverline = $derived(headerOverlineProp ?? overline);

  const hasHeader = $derived(headerOverline || headerHeadline || slots.logo);
  const hasFooter = $derived(footerHeadline || footerText);

  const mainHeadlineElement = $derived(headerHeadlineLevel ? `h${headerHeadlineLevel}` : "span");
  const footerHeadlineElement = $derived(footerHeadlineLevel ? `h${footerHeadlineLevel}` : "span");

  const allAriaVisibleElements = $derived(ocAriaVisibleElements!);

  const isAriaHidden = (element: OcAriaVisibleElement): boolean => {
    return !allAriaVisibleElements.includes(element);
  };
</script>

<BasicCardV2
  {Host}
  {asButton}
  bind:href
  bind:base64Href
  class={`promo-card ${inverted ? "promo-card--inverted" : ""}`}
  {rel}
  {target}
  {elevationLevel}
  {inverted}
  {backgroundColor}
>
  {#snippet children(onslotchange)}
    <slot name="primary-action" {onslotchange} />
    {#if hasHeader}
      <header class="promo-card__header">
        <div class="promo-card__header-container">
          {#if headerOverline}
            <span
              class="promo-card__header-container-overline"
              aria-hidden={isAriaHidden("headerOverline")}>{headerOverline}</span
            >
          {/if}
          {#if headerHeadline}
            <svelte:element
              this={mainHeadlineElement}
              class="promo-card__header-container-main-headline"
              aria-hidden={isAriaHidden("headerHeadline")}
            >
              {headerHeadline}
            </svelte:element>
          {/if}
        </div>

        {#if slots["header-logo"] || slots.logo}
          <span class="promo-card__header-logo-outer">
            <slot name="logo" aria-hidden={isAriaHidden("logo")} />
            <slot name="header-logo" aria-hidden={isAriaHidden("headerLogo")} />
          </span>
        {/if}

        {#if slots["action-signifier-header"]}
          <slot name="action-signifier-header" />
        {/if}
      </header>
    {/if}

    <div class="promo-card__body">
      <slot aria-hidden={isAriaHidden("mainContent")} />
      {#if slots.tags}
        <div class="promo-card__tag-container">
          <slot name="tags" aria-hidden={isAriaHidden("tags")} />
        </div>
      {/if}
    </div>

    {#if hasFooter}
      <footer
        class="promo-card__footer"
        class:promo-card__footer--with-action-signifier-footer={!!slots["action-signifier-footer"]}
      >
        <div>
          {#if footerHeadline}
            <svelte:element
              this={footerHeadlineElement}
              class="promo-card__footer-headline"
              aria-hidden={isAriaHidden("footerHeadline")}
            >
              {footerHeadline}
            </svelte:element>
          {/if}
          {#if footerText}
            <span class="promo-card__footer-text" aria-hidden={isAriaHidden("footerText")}
              >{footerText}</span
            >
          {/if}
        </div>

        {#if slots["action-signifier-footer"]}
          <span class="promo-card__footer-action-signifier">
            <slot name="action-signifier-footer" />
          </span>
        {/if}
      </footer>
    {/if}
  {/snippet}
</BasicCardV2>

<DefaultStyles />

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";
  :global {
    :host {
      display: block;
      position: relative;
      container-type: inline-size;

      /*        */
      --background-color: initial;
    }

    .promo-card.promo-card {
      display: flex;
      flex-direction: column;
    }

    .promo-card.promo-card.promo-card--inverted {
      color: tokens.$oc-component-promo-card-inverted-text-color;
    }

    .promo-card__header {
      display: flex;
      flex-direction: row;
      gap: tokens.$oc-component-promo-card-gap-logo;
      padding: tokens.$oc-component-promo-card-spacing-x tokens.$oc-component-promo-card-spacing-y
        tokens.$oc-component-promo-card-header-spacing-bottom;
      align-items: center;

      width: 100%;
      height: tokens.$oc-component-promo-card-s-m-header-height;

      @include mixins.breakpoint-from-l(false, $containerFirst: true) {
        height: tokens.$oc-component-promo-card-l-xl-header-height;
      }

      &-logo-outer {
        display: block;
        height: tokens.$oc-component-promo-card-logo-height;
        padding: tokens.$oc-component-promo-card-logo-indent;
        box-sizing: content-box;
        background-color: var(
          --background-color,
          tokens.$oc-component-promo-card-logo-default-background-color
        );
        border-radius: 4px;
      }

      &-container {
        display: flex;
        flex-direction: column;
        justify-content: center;

        /*                           */
        flex: 1;

        &,
        & * {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        &-overline {
          font: tokens.$oc-component-promo-card-overline-font;
        }

        &-main-headline {
          font: tokens.$oc-component-promo-card-s-m-first-headline-font;
          @include mixins.breakpoint-from-l(false, $containerFirst: true) {
            font: tokens.$oc-component-promo-card-l-xl-first-headline-font;
          }
        }
      }
    }

    /*               */
    .promo-card__body {
      position: relative;
      overflow: hidden;
      margin: tokens.$oc-component-promo-card-image-spacing;
    }

    .promo-card__tag-container {
      position: absolute;
      bottom: 0;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      gap: 8px;
      padding: tokens.$oc-component-promo-card-tag-container-spacing-y
        tokens.$oc-component-promo-card-tag-container-spacing-x;
    }

    .promo-card__footer {
      display: flex;
      flex-direction: column;
      padding: tokens.$oc-component-promo-card-footer-spacing-top
        tokens.$oc-component-promo-card-spacing-x tokens.$oc-component-promo-card-spacing-y;
      &--with-action-signifier-footer {
        flex-direction: row;
        justify-content: space-between;
      }
      & &-action-signifier {
        align-self: center;
      }

      &-headline {
        /*                         */
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        display: -webkit-box;
        overflow: hidden;

        font: tokens.$oc-component-promo-card-s-m-second-headline-font;
        @include mixins.breakpoint-from-l(false, $containerFirst: true) {
          font: tokens.$oc-component-promo-card-l-xl-second-headline-font;
        }
      }

      &-text {
        font: tokens.$oc-component-promo-card-text-font;
      }
    }
  }
</style>
